import localforage from "localforage";
import axios from "axios";
import { setupCache, buildStorage } from "axios-cache-interceptor";
import ApiException from "./ApiException.js";

class Api {
    static CACHE_DRIVER = {
        AUTO: 'AUTO',
        NOCACHE: 'NOCACHE',
        WEBSQL: 'WEBSQL',
        INDEXEDDB: 'INDEXEDDB',
        LOCALSTORAGE: 'LOCALSTORAGE'
    };

    noCache = false;
    dataCache = null;
    gateway = null;

    constructor(baseUrl,
                cacheDriver = Api.CACHE_DRIVER.AUTO,
                protectedKeyFields = ['password']) {

        const dataCacheConfig = {
            name      : baseUrl,
            storeName : 'DataCache'
        };

        let noCache = false;
        switch (cacheDriver) {
            case Api.CACHE_DRIVER.WEBSQL:
                dataCacheConfig.driver = localforage.WEBSQL;
                break;
            case Api.CACHE_DRIVER.INDEXEDDB:
                dataCacheConfig.driver = localforage.INDEXEDDB;
                break;
            case Api.CACHE_DRIVER.LOCALSTORAGE:
                dataCacheConfig.driver = localforage.LOCALSTORAGE;
                break;
            case Api.CACHE_DRIVER.NOCACHE:
                noCache = true;
                break;
            case Api.CACHE_DRIVER.AUTO:
            default:
                dataCacheConfig.driver = [localforage.INDEXEDDB, localforage.WEBSQL, localforage.LOCALSTORAGE];
                break;
        }

        const dataCache = localforage.createInstance(dataCacheConfig);
        if (!noCache) {
            dataCache.ready()
                .then(() => console.log(
                    `Api Data Cache initialized: driver=${dataCache.driver()}, name=${dataCacheConfig.name}`))
                .catch(e => {
                    noCache = true;
                    console.warn("Cache init failed, disabling cache:", e);
                });
        } else {
            console.log("Api responses will not be cached.");
        }

        const api = axios.create({
            baseURL: baseUrl,
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
        });

        const sanitizedKeyGen = config => {
            const { method, url, data, params, headers } = config;
            const safeData = data ? { ...data } : {};
            protectedKeyFields.forEach(f =>
                    data && Object.keys(safeData).forEach(k => {
                        if (k.toLowerCase() === f.toLowerCase()) delete safeData[k];
                    })
            );
            return JSON.stringify({ method, url, data: safeData, params, headers });
        };

        this.noCache = noCache;
        this.dataCache = dataCache;
        if (noCache) {
            this.gateway = api;
        } else {
            const storage = buildStorage({
                async find(key) {
                    const wrapped = await dataCache.getItem(key);
                    if (!wrapped) return undefined;
                    if (Date.now() > wrapped.expiresAt) {
                        await dataCache.removeItem(key);
                        return undefined;
                    }
                    return wrapped.value;
                },
                async set(key, value, req) {
                    const ttl = value.ttl ?? (req?.cache?.ttl ?? 0);
                    const expiresAt = Date.now() + ttl;
                    await dataCache.setItem(key, { value, expiresAt });
                },
                async remove(key) {
                    await dataCache.removeItem(key);
                }
            });

            this.gateway = setupCache(api, {
                storage,
                generateKey: sanitizedKeyGen,
                ttl: 1000 * 60 * 5, // 5 minutes
                methods: ['get'],
                cachePredicate: { statusCheck: s => s >= 200 && s < 300 },
                cacheTakeover: false
            });
        }
    }

    async applyCacheVersion(requiredVersion) {
        if (requiredVersion && !this.noCache) {
            console.log(`Applying Api Data Cache version ${requiredVersion}`);
            const found = await this.dataCache.getItem('__version__');
            if (!found || found < requiredVersion) {
                console.log(`Cache expired (found ${found}). Clearing and setting version.`);
                await this.clearDataCache();
                await this.dataCache.setItem('__version__', requiredVersion);
            }
        }
    }

    async executeNoCacheRequest(request) {
        try {
            const resp = await this.gateway.request({ ...request, cache: false });
            return resp;
        } catch (e) {
            console.error(new ApiException(e));
            throw new ApiException(e);
        }
    }

    async executeRequest(request) {
        if (this.noCache) return this.executeNoCacheRequest(request);
        try {
            return await this.gateway.request(request);
        } catch (e) {
            console.error(new ApiException(e));
            throw new ApiException(e);
        }
    }

    async clearDataCache(key = null) {
        if (key) {
            await this.dataCache.removeItem(key);
        } else {
            await this.dataCache.clear();
        }
    }
}

export default Api;
